import type { PropsWithChildren } from 'react'
import { createContext, Suspense, useContext, useEffect, useState } from 'react'
import { runInAction } from 'mobx'
import moize from 'moize'
import { keepPreviousData } from '@tanstack/react-query'

import { createEmptyPathItem } from '@jotta/file-utils'
import { useSuspenseCustomer } from '@jotta/grpc-connect-client/customer'
import { parseGetPathParams } from '@jotta/grpc-js-client/files/getPath'
import type { GetPathItemResponse } from '@jotta/grpc-js-client/files/getPathItem'
import { ItemType } from '@jotta/grpc-js-client/fileService'
import { getRouterStore } from '@jotta/router'
import { AppError } from '@jotta/types/AppError'
import type { PathItemObject } from '@jotta/types/Files'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { useBrandTheme } from '@jotta/ui/useBrandTheme'
import { createPath, getFilenameFromPath } from '@jotta/utils/pathUtils'

import type { parseFileRouteParams } from '../FileRoute/utils/parseFileRouteParams'
import { useParsedFileRouteParams } from '../FileRoute/utils/parseFileRouteParams'
import { usePathItem } from '../PathItem/hooks/usePathItem'

import { FileStore } from './FileStore'

export interface FileContextValue {
  fileStore: FileStore
}

export const FileContext = createContext<FileContextValue | undefined>(
  undefined,
)

const createFakePathItemFromParsedRoute = moize(
  function createPathItemFromParsedRoute(
    route: ReturnType<typeof parseFileRouteParams>,
    owner: string,
  ): GetPathItemResponse {
    const item: PathItemObject = {
      ...createEmptyPathItem(),
      name: route.isRoot ? route.context : getFilenameFromPath(route.apiPath),
      path: route.apiPath,
      type: ItemType.FOLDER,
      owner,
    }
    return {
      children: [],
      cursor: '',
      entries: {},
      ids: [],
      isFile: false,
      isFolder: true,
      item,
      pathItem: item,
      uniquePath: createPath('/', owner, route.apiPath),
      options: parseGetPathParams(route.getPathParams),
    }
  },
  {
    maxSize: 100,
  },
)
export function FileContextProvider({
  children,
  fileStore,
}: PropsWithChildren<Partial<FileContextValue>>) {
  const { brandStore } = useBrandTheme()
  const router = getRouterStore()
  const route = useParsedFileRouteParams()
  const { apiPath, context } = route
  const isSearch = context === 'search'
  const [store, setStore] = useState(fileStore)

  const query = usePathItem({
    variables: {
      context,
      path: apiPath,
    },
    enabled: !isSearch,
    retry: false,
    placeholderData: keepPreviousData,
    throwOnError: true,
  })

  const { customer } = useSuspenseCustomer().data
  const username = customer?.username || ''
  const data = isSearch
    ? createFakePathItemFromParsedRoute(
        route,
        username,
      ) /* NB: here we create a fake path item representing the search path, but this doesn't correspond with something we can fetch from our apis and this creates problems down the line. Try finding a better approach when eliminating the FileStore */
    : query.data

  useEffect(() => {
    if (!data) {
      return
    }

    if (fileStore) {
      fileStore.currentItem = data
    } else {
      const store = new FileStore(data, brandStore, router)
      store.username = username
      runInAction(() => {
        store.folder.addItem(data.item, Date.now())
      })
      setStore(store)
    }
    // eslint-disable-next-line
  }, [brandStore, data, fileStore, router])

  useEffect(() => {
    if (store) {
      store.username = username
    }
  }, [store, username])

  if (!store) {
    return <LoadingOverlay id="FileContext" open />
  }

  return (
    <Suspense fallback={<LoadingOverlay id="FileContext" open />}>
      <FileContext.Provider
        value={{
          fileStore: store,
        }}
      >
        {children}
      </FileContext.Provider>
    </Suspense>
  )
}

export function useFileContext() {
  const fileContext = useContext(FileContext)
  if (!fileContext) {
    throw new AppError({
      message: 'useFileContext must be used inside FileContextProvider',
    })
  }
  return fileContext
}
export function useFileStore() {
  const fileContext = useFileContext()
  return fileContext.fileStore
}
